// src/components/roles/RoleAssignmentDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  alpha,
  Avatar,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import useRoleStore from '../../stores/roleStore';
import useUserStore from '../../stores/userStore';

// Import stores and services

// Styled components
const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiChip-label': {
    fontWeight: 500,
  },
}));

const RoleChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.secondary.main, 0.15),
  color: theme.palette.secondary.dark,
  fontWeight: 600,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  minWidth: 400,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 150,
    height: 150,
    borderRadius: '50%',
    background: alpha(theme.palette.primary.main, 0.05),
    transform: 'translate(30%, -30%)',
    zIndex: 0,
  },
}));

/**
 * Dialog for assigning roles to users
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Close callback
 * @param {string} [props.roleId] - Role ID to assign (if preselected)
 */
const RoleAssignmentDialog = ({ open, onClose, roleId }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { roles, loading: rolesLoading, assignRoleToUser } = useRoleStore();
  const { users: userList, fetchUsers, loading: userStoreLoading } = useUserStore();
  
  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      if (open && users.length === 0) {
        try {
          setUsersLoading(true);
          setError(null);
          
          await fetchUsers();
          
          setUsersLoading(false);
        } catch (err) {
          console.error('Failed to fetch users', err);
          setError('Failed to load users. Please try again.');
          setUsersLoading(false);
        }
      }
    };
    
    loadUsers();
  }, [open, fetchUsers]);
  
  // Update users when userList changes
  useEffect(() => {
    if (userList && userList.length > 0) {
      setUsers(userList);
    }
  }, [userList]);
  
  // Set initial role if roleId is provided
  useEffect(() => {
    if (roleId && roles.length > 0) {
      const role = roles.find(r => r.id === roleId);
      setSelectedRole(role || null);
    }
  }, [roleId, roles]);
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setError(null);
      setSuccess(false);
      if (!roleId) {
        setSelectedRole(null);
      }
    }
  }, [open, roleId]);
  
  // Handle assigning role
  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) {
      setError('Please select both a user and a role');
      return;
    }
    
    try {
      setError(null);
      setSuccess(false);
      
      await assignRoleToUser(selectedUser.id, selectedRole.id);
      
      setSuccess(true);
      
      // Reset selected user but keep role if preselected
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to assign role', err);
      setError(err.message || 'Failed to assign role. Please try again.');
    }
  };
  
  // Handle dialog close
  const handleClose = () => {
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <PersonAddIcon sx={{ mr: 1.5 }}  />
        Assign Role to User
      </DialogTitle>
      
      <StyledDialogContent>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            onClose={() => setSuccess(false)}
          >
            Role assigned successfully!
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select User
          </Typography>
          
          <Autocomplete
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
            }}
            options={users}
            loading={usersLoading || userStoreLoading}
            getOptionLabel={(option) => option ? `${option.firstName || ''} ${option.lastName || ''} (${option.email})` : ''}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{ 
                    width: 28, 
                    height: 28, 
                    mr: 1.5,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.8)
                  }}
                >
                  {option.firstName[0]}{option.lastName[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2">
                    {option.firstName} {option.lastName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          
          {selectedUser && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="textSecondary">
                Current Role:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {selectedUser.roleId || selectedUser.role ? (
                  <StyledChip
                    icon={<SecurityIcon />}
                    label={selectedUser.roleName || selectedUser.role || 'Unknown Role'}
                    size="small"
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No role assigned
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Select Role
          </Typography>
          
          <Autocomplete
            value={selectedRole}
            onChange={(event, newValue) => {
              setSelectedRole(newValue);
            }}
            options={roles}
            loading={rolesLoading}
            getOptionLabel={(option) => option.name}
            disabled={Boolean(roleId)} // Disable if roleId is provided
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2">
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option.key} • {option.permissions?.length || 0} permissions
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {rolesLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          
          {selectedRole && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="textSecondary">
                Role Description:
              </Typography>
              <Typography variant="body2" paragraph sx={{ mt: 0.5 }}>
                {selectedRole.description}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                <RoleChip
                  label={`${selectedRole.permissions?.length || 0} permissions`}
                  size="small"
                  icon={<SecurityIcon />}
                />
              </Box>
            </Box>
          )}
        </Box>
      </StyledDialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          
          onClick={handleAssignRole}
          disabled={!selectedUser || !selectedRole}
          startIcon={<PersonAddIcon />}
        >
          Assign Role
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleAssignmentDialog;